import { pgTable, text, serial, integer, doublePrecision, timestamp, boolean, json, jsonb, varchar, uniqueIndex, index } from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

export const users = pgTable("users", {
  id: serial("id").primaryKey(),
  username: text("username").notNull().unique(),
  password: text("password"),
  role: text("role").notNull().default("user"),
  email: text("email"),
  honeyBalance: integer("honey_balance").notNull().default(0),
  dateOfBirth: text("date_of_birth"),
  gender: text("gender"),
  lastVenueId: integer("last_venue_id"),
  lastSeenAt: timestamp("last_seen_at"),
  googleId: text("google_id").unique(),
  appleId: text("apple_id").unique(),
  displayName: text("display_name"),
  avatarUrl: text("avatar_url"),
  onboardingComplete: boolean("onboarding_complete").notNull().default(false),
  magicLinkToken: text("magic_link_token"),
  magicLinkExpiry: timestamp("magic_link_expiry"),
  profilePhotoUrl: text("profile_photo_url"),
  notificationMessage: text("notification_message"),
  notificationRead: boolean("notification_read").notNull().default(true),
  notifyFriendRequests: boolean("notify_friend_requests").notNull().default(true),
  notifyFriendVibes: boolean("notify_friend_vibes").notNull().default(true),
  notifyVenueAlerts: boolean("notify_venue_alerts").notNull().default(true),
  notifyMediaReactions: boolean("notify_media_reactions").notNull().default(true),
}, (table) => ({
  usernameLowerUnique: uniqueIndex("users_username_lower_unique").on(sql`lower(${table.username})`),
}));

export const venues = pgTable("venues", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(),
  type: text("type").notNull(),
  description: text("description"),
  latitude: doublePrecision("latitude").notNull(),
  longitude: doublePrecision("longitude").notNull(),
  capacityLevel: integer("capacity_level").notNull().default(0),
  temperature: integer("temperature").notNull().default(50),
  reportCount: integer("report_count").notNull().default(0),
  isSponsored: boolean("is_sponsored").notNull().default(false),
  lastUpdated: timestamp("last_updated").defaultNow(),
  googlePlaceId: text("google_place_id"),
  googleRating: doublePrecision("google_rating"),
  googleRatingCount: integer("google_rating_count"),
  googlePriceLevel: text("google_price_level"),
  openingHours: jsonb("opening_hours"),
  instagramUrl: text("instagram_url"),
  tiktokUrl: text("tiktok_url"),
  websiteUrl: text("website_url"),
  logoUrl: text("logo_url"),
  coverPhotoUrl: text("cover_photo_url"),
  noPhotosAt: timestamp("no_photos_at"),
  noPhotosCount: integer("no_photos_count").default(0),
  noHistogramAt: timestamp("no_histogram_at"),
  noHistogramCount: integer("no_histogram_count").default(0),
  street: text("street"),
  postcode: text("postcode"),
});

export const venueEvents = pgTable("venue_events", {
  id: serial("id").primaryKey(),
  venueId: integer("venue_id").notNull(),
  title: text("title").notNull(),
  dayOfWeek: text("day_of_week").notNull(),
  time: text("time").notNull(),
  description: text("description"),
  tag: text("tag"),
});

export const venueAssignments = pgTable("venue_assignments", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull(),
  venueId: integer("venue_id").notNull(),
});

// Session table managed at runtime by connect-pg-simple. Declared here so
// `drizzle-kit push` does not try to drop it (which would abort the push).
export const sessions = pgTable("session", {
  sid: varchar("sid").primaryKey(),
  sess: json("sess").notNull(),
  expire: timestamp("expire", { precision: 6, mode: "date" }).notNull(),
}, (t) => ({
  expireIdx: index("IDX_session_expire").on(t.expire),
}));

export const venueViews = pgTable("venue_views", {
  id: serial("id").primaryKey(),
  venueId: integer("venue_id").notNull(),
  // Nullable so anonymous /api/venues/:id/view calls keep working; populated when
  // the view comes from an authenticated session.
  userId: integer("user_id").references(() => users.id),
  viewedAt: timestamp("viewed_at").defaultNow(),
});

export const venueReports = pgTable("venue_reports", {
  id: serial("id").primaryKey(),
  venueId: integer("venue_id").notNull(),
  capacityLevel: integer("capacity_level").notNull(),
  vibe: text("vibe").notNull(),
  reportedAt: timestamp("reported_at").defaultNow(),
  userId: integer("user_id"),
  latitude: doublePrecision("latitude"),
  longitude: doublePrecision("longitude"),
  reportSource: text("report_source"),
});

// NOTE: There is intentionally no DB-level race-proof cooldown index on
// venue_reports. An expression index over (user_id, venue_id,
// floor(extract(epoch from reported_at) / 7200)) cannot be represented
// correctly by drizzle-kit OR Replit's publish-time migration generator
// (both emit `numeric_ops` opclass against the integer columns and Postgres
// rejects it). The vibe-report cooldown is enforced application-side in
// server/honey.ts via the SELECT-then-INSERT check; concurrent inserts in
// the same millisecond can theoretically slip past it but are rare.

export const favourites = pgTable("favourites", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull(),
  venueId: integer("venue_id").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

export const googlePopularTimesCache = pgTable("google_popular_times_cache", {
  venueId: integer("venue_id").primaryKey().references(() => venues.id),
  histogram: jsonb("histogram"),
  fetchedAt: timestamp("fetched_at").notNull().defaultNow(),
});

export const venuePhotos = pgTable("venue_photos", {
  id: serial("id").primaryKey(),
  venueId: integer("venue_id").notNull(),
  googlePhotoName: text("google_photo_name"),
  photoUrl: text("photo_url"),
  widthPx: integer("width_px"),
  heightPx: integer("height_px"),
  attributions: text("attributions"),
  cachedAt: timestamp("cached_at").defaultNow(),
  staleAt: timestamp("stale_at"),
}, (t) => ({
  venuePhotoUrlUniq: uniqueIndex("venue_photos_venue_photo_url_uniq").on(t.venueId, t.photoUrl),
}));

export const userVenuePhotos = pgTable("user_venue_photos", {
  id: serial("id").primaryKey(),
  venueId: integer("venue_id").notNull(),
  userId: integer("user_id"),
  photoData: text("photo_data").notNull(),
  uploadedAt: timestamp("uploaded_at").defaultNow(),
});

export const userMedia = pgTable("user_media", {
  id: serial("id").primaryKey(),
  venueId: integer("venue_id").notNull().references(() => venues.id),
  userId: integer("user_id").notNull().references(() => users.id),
  mediaType: varchar("media_type", { length: 10 }).notNull(),
  cloudinaryUrl: text("cloudinary_url").notNull(),
  cloudinaryPublicId: text("cloudinary_public_id").notNull(),
  thumbnailUrl: text("thumbnail_url"),
  durationSeconds: integer("duration_seconds"),
  thumbsUp: integer("thumbs_up").notNull().default(0),
  thumbsDown: integer("thumbs_down").notNull().default(0),
  removed: boolean("removed").notNull().default(false),
  createdAt: timestamp("created_at").notNull().defaultNow(),
})

export const mediaReactions = pgTable("media_reactions", {
  id: serial("id").primaryKey(),
  mediaId: integer("media_id").notNull().references(() => userMedia.id),
  userId: integer("user_id").notNull().references(() => users.id),
  reaction: varchar("reaction", { length: 20 }).notNull(),
  createdAt: timestamp("created_at").notNull().defaultNow(),
})

export const honeyTransactions = pgTable("honey_transactions", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull(),
  amount: integer("amount").notNull(),
  reason: text("reason").notNull(),
  venueId: integer("venue_id"),
  specialId: integer("special_id"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const venueSpecials = pgTable("venue_specials", {
  id: serial("id").primaryKey(),
  venueId: integer("venue_id").notNull(),
  title: text("title").notNull(),
  description: text("description").notNull(),
  honeyCost: integer("honey_cost").notNull().default(0),
  active: boolean("active").notNull().default(true),
  expiresAt: timestamp("expires_at"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const redemptions = pgTable("redemptions", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull(),
  specialId: integer("special_id").notNull(),
  venueId: integer("venue_id").notNull(),
  honeySpent: integer("honey_spent").notNull(),
  redeemedAt: timestamp("redeemed_at").defaultNow(),
});

export const businessApplications = pgTable("business_applications", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull(),
  businessName: text("business_name").notNull(),
  venueId: integer("venue_id").notNull(),
  applicantRole: text("applicant_role").notNull(),
  businessEmail: text("business_email"),
  companiesHouseNumber: text("companies_house_number"),
  status: text("status").notNull().default("pending"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const capacitySourceValues = [
  "serpapi_histogram",
  "claude_estimate",
  "none",
] as const;
export type CapacitySource = (typeof capacitySourceValues)[number];

export const venueSnapshots = pgTable("venue_snapshots", {
  id: serial("id").primaryKey(),
  venueId: integer("venue_id").notNull().references(() => venues.id).unique(),
  capacityPercent: integer("capacity_percent").notNull(),
  capacityLabel: varchar("capacity_label", { length: 16 }).notNull(),
  colourCode: varchar("colour_code", { length: 16 }).notNull(),
  isOpen: boolean("is_open").notNull().default(false),
  dataConfidence: integer("data_confidence"),
  source: text("source").$type<CapacitySource>(),
  lastGoogleBusyness: integer("last_google_busyness"),
  snapshotAt: timestamp("snapshot_at").notNull(),
  expiresAt: timestamp("expires_at").notNull(),
});

export const venueSnapshotHistory = pgTable("venue_snapshot_history", {
  id: serial("id").primaryKey(),
  venueId: integer("venue_id").notNull().references(() => venues.id),
  capacityPercent: integer("capacity_percent").notNull(),
  isOpen: boolean("is_open").notNull().default(false),
  snapshotAt: timestamp("snapshot_at").notNull().defaultNow(),
  confidence: integer("confidence"),
  primarySource: text("primary_source").$type<CapacitySource>(),
  googleHistogramScore: integer("google_histogram_score"),
  claudeScore: integer("claude_score"),
  userReportCount: integer("user_report_count"),
  userReportAvg: doublePrecision("user_report_avg"),
  userReportWeight: doublePrecision("user_report_weight"),
});

// ─── Capacity source histories and scheduler state ──────────────────────────

export const claudeEstimateCache = pgTable("claude_estimate_cache", {
  id: serial("id").primaryKey(),
  venueId: integer("venue_id").notNull().references(() => venues.id),
  dayOfWeek: integer("day_of_week").notNull(),
  hourOfDay: integer("hour_of_day").notNull(),
  estimatedScore: integer("estimated_score").notNull(),
  cachedAt: timestamp("cached_at").notNull().defaultNow(),
}, (table) => ({
  venueDayHourUnique: uniqueIndex("claude_estimate_cache_venue_day_hour_unique")
    .on(table.venueId, table.dayOfWeek, table.hourOfDay),
}));

export const googleHistogramHistory = pgTable("google_histogram_history", {
  id: serial("id").primaryKey(),
  venueId: integer("venue_id").notNull().references(() => venues.id),
  histogram: jsonb("histogram"),
  fetchedAt: timestamp("fetched_at").notNull().defaultNow(),
});

export const claudeEstimateHistory = pgTable("claude_estimate_history", {
  id: serial("id").primaryKey(),
  venueId: integer("venue_id").notNull().references(() => venues.id),
  dayOfWeek: integer("day_of_week").notNull(),
  hourOfDay: integer("hour_of_day").notNull(),
  estimatedScore: integer("estimated_score").notNull(),
  source: text("source").$type<CapacitySource>().notNull().default("claude_estimate"),
  modelVersion: text("model_version").notNull(),
  promptVersion: text("prompt_version").notNull(),
  recordedAt: timestamp("recorded_at").notNull().defaultNow(),
});

export const schedulerState = pgTable("scheduler_state", {
  key: text("key").primaryKey(),
  lastRunAt: timestamp("last_run_at"),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const events = pgTable("events", {
  id: serial("id").primaryKey(),
  venueId: integer("venue_id").references(() => venues.id, { onDelete: "cascade" }),
  title: text("title").notNull(),
  description: text("description"),
  startTime: timestamp("start_time").notNull(),
  endTime: timestamp("end_time"),
  imageUrl: text("image_url"),
  ticketUrl: text("ticket_url"),
  source: text("source").notNull(),
  externalId: text("external_id"),
  raVenueName: text("ra_venue_name"),
  raVenueAddress: text("ra_venue_address"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertEventSchema = createInsertSchema(events).omit({ id: true, createdAt: true }).extend({
  title: z.string().trim().min(1, "Title required").max(200, "Title must be at most 200 characters"),
  description: z.string().trim().max(2000, "Description must be at most 2000 characters").nullable().optional(),
  imageUrl: z.string().trim().max(500).url("imageUrl must be a valid URL").nullable().optional(),
  ticketUrl: z.string().trim().max(500).url("ticketUrl must be a valid URL").nullable().optional(),
  externalId: z.string().trim().max(100).nullable().optional(),
  raVenueName: z.string().trim().max(200).nullable().optional(),
  raVenueAddress: z.string().trim().max(500).nullable().optional(),
  source: z.enum(["business", "resident_advisor"]),
});
export type Event = typeof events.$inferSelect;
export type InsertEvent = z.infer<typeof insertEventSchema>;

export const friendships = pgTable("friendships", {
  id: serial("id").primaryKey(),
  requesterId: integer("requester_id").notNull().references(() => users.id),
  addresseeId: integer("addressee_id").notNull().references(() => users.id),
  status: varchar("status", { length: 20 }).notNull().default("pending"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const friendLocationSharing = pgTable("friend_location_sharing", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => users.id),
  friendId: integer("friend_id").notNull().references(() => users.id),
  enabled: boolean("enabled").notNull().default(false),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  uniqueIndex("friend_location_sharing_user_friend_idx").on(table.userId, table.friendId),
]);

export const pushSubscriptions = pgTable("push_subscriptions", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => users.id),
  subscription: jsonb("subscription").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertFriendshipSchema = createInsertSchema(friendships).omit({ id: true, createdAt: true });
export const insertFriendLocationSharingSchema = createInsertSchema(friendLocationSharing).omit({ id: true, updatedAt: true });
export const insertPushSubscriptionSchema = createInsertSchema(pushSubscriptions).omit({ id: true, createdAt: true });

export const insertVenuePhotoSchema = createInsertSchema(venuePhotos).omit({ id: true, cachedAt: true });
export const insertUserVenuePhotoSchema = createInsertSchema(userVenuePhotos).omit({ id: true, uploadedAt: true });
export const insertUserMediaSchema = createInsertSchema(userMedia).omit({ id: true, createdAt: true, thumbsUp: true, thumbsDown: true, removed: true });
export const insertMediaReactionSchema = createInsertSchema(mediaReactions).omit({ id: true, createdAt: true });

export const insertFavouriteSchema = createInsertSchema(favourites).omit({ id: true, createdAt: true });

export const insertVenueViewSchema = createInsertSchema(venueViews).omit({ id: true, viewedAt: true });
export const insertVenueReportSchema = createInsertSchema(venueReports).omit({ id: true, reportedAt: true });

export const insertUserSchema = createInsertSchema(users).omit({ id: true }).extend({
  username: z.string().trim().min(3, "Username must be at least 3 characters").max(50, "Username must be at most 50 characters"),
  password: z.string().min(6, "Password must be at least 6 characters").max(200, "Password too long"),
  role: z.enum(["user", "business", "admin"]).default("user"),
  email: z.string().trim().email("Please enter a valid email").max(254).optional().nullable(),
  displayName: z.string().trim().max(100).optional().nullable(),
  dateOfBirth: z.string().trim().max(20).optional().nullable(),
  gender: z.string().trim().max(40).optional().nullable(),
  avatarUrl: z.string().trim().max(500).optional().nullable(),
  profilePhotoUrl: z.string().trim().max(500).optional().nullable(),
  notificationMessage: z.string().trim().max(500).optional().nullable(),
});

export const insertVenueSchema = createInsertSchema(venues).omit({
  id: true,
  lastUpdated: true
}).extend({
  name: z.string().trim().min(1, "Venue name required").max(200, "Venue name must be at most 200 characters"),
  type: z.enum(["bar", "pub", "club", "hotel"]),
  description: z.string().trim().max(2000, "Description must be at most 2000 characters").nullable().optional(),
  latitude: z.number().min(-90, "Latitude must be between -90 and 90").max(90, "Latitude must be between -90 and 90"),
  longitude: z.number().min(-180, "Longitude must be between -180 and 180").max(180, "Longitude must be between -180 and 180"),
  instagramUrl: z.string().trim().max(500).url("instagramUrl must be a valid URL").nullable().optional(),
  tiktokUrl: z.string().trim().max(500).url("tiktokUrl must be a valid URL").nullable().optional(),
  websiteUrl: z.string().trim().max(500).url("websiteUrl must be a valid URL").nullable().optional(),
  logoUrl: z.string().trim().max(500).url("logoUrl must be a valid URL").nullable().optional(),
  coverPhotoUrl: z.string().trim().max(500).url("coverPhotoUrl must be a valid URL").nullable().optional(),
});

export const insertVenueEventSchema = createInsertSchema(venueEvents).omit({
  id: true,
}).extend({
  title: z.string().trim().min(1, "Title required").max(200, "Title must be at most 200 characters"),
  dayOfWeek: z.string().trim().max(20),
  time: z.string().trim().max(20),
  description: z.string().trim().max(2000).nullable().optional(),
  tag: z.string().trim().max(50).nullable().optional(),
});

export const insertVenueAssignmentSchema = createInsertSchema(venueAssignments).omit({
  id: true,
});

export const insertHoneyTransactionSchema = createInsertSchema(honeyTransactions).omit({ id: true, createdAt: true });

export const insertVenueSpecialSchema = createInsertSchema(venueSpecials).omit({ id: true, createdAt: true }).extend({
  title: z.string().trim().min(3, "Title must be at least 3 characters").max(200, "Title must be at most 200 characters"),
  description: z.string().trim().min(10, "Description must be at least 10 characters").max(2000, "Description must be at most 2000 characters"),
  honeyCost: z.number().int().min(0).max(1_000_000),
  expiresAt: z.string().datetime().optional().nullable(),
});

export const insertRedemptionSchema = createInsertSchema(redemptions).omit({ id: true, redeemedAt: true });

export const insertBusinessApplicationSchema = createInsertSchema(businessApplications).omit({ id: true, createdAt: true, status: true }).extend({
  businessName: z.string().trim().min(1, "Business name required").max(200, "Business name must be at most 200 characters"),
  applicantRole: z.string().trim().min(1, "Applicant role required").max(100, "Applicant role must be at most 100 characters"),
  businessEmail: z.string().trim().email("Invalid business email").max(254).optional().nullable(),
  companiesHouseNumber: z.string().trim().max(30, "Companies House number must be at most 30 characters").optional().nullable(),
});

export const insertVenueSnapshotSchema = createInsertSchema(venueSnapshots).omit({ id: true }).extend({
  capacityLabel: z.enum(["quiet", "average", "busy", "very busy"]),
  colourCode: z.enum(["blue", "cyan-green", "lime", "yellow", "orange", "red"]),
  dataConfidence: z.number().int().min(0).max(100).nullable().optional(),
});

export type User = typeof users.$inferSelect;
export type InsertUser = z.infer<typeof insertUserSchema>;
export type Venue = typeof venues.$inferSelect;
export type InsertVenue = z.infer<typeof insertVenueSchema>;
export type VenueEvent = typeof venueEvents.$inferSelect;
export type InsertVenueEvent = z.infer<typeof insertVenueEventSchema>;
export type VenueAssignment = typeof venueAssignments.$inferSelect;
export type InsertVenueAssignment = z.infer<typeof insertVenueAssignmentSchema>;
export type VenueView = typeof venueViews.$inferSelect;
export type InsertVenueView = z.infer<typeof insertVenueViewSchema>;
export type VenueReport = typeof venueReports.$inferSelect;
export type InsertVenueReport = z.infer<typeof insertVenueReportSchema>;
export type Favourite = typeof favourites.$inferSelect;
export type InsertFavourite = z.infer<typeof insertFavouriteSchema>;

export type VenuePhoto = typeof venuePhotos.$inferSelect;
export type InsertVenuePhoto = z.infer<typeof insertVenuePhotoSchema>;
export type UserVenuePhoto = typeof userVenuePhotos.$inferSelect;
export type InsertUserVenuePhoto = z.infer<typeof insertUserVenuePhotoSchema>;
export type UserMedia = typeof userMedia.$inferSelect;
export type InsertUserMedia = z.infer<typeof insertUserMediaSchema>;
export type MediaReaction = typeof mediaReactions.$inferSelect;
export type InsertMediaReaction = z.infer<typeof insertMediaReactionSchema>;
export type HoneyTransaction = typeof honeyTransactions.$inferSelect;
export type InsertHoneyTransaction = z.infer<typeof insertHoneyTransactionSchema>;
export type VenueSpecial = typeof venueSpecials.$inferSelect;
export type InsertVenueSpecial = z.infer<typeof insertVenueSpecialSchema>;
export type Redemption = typeof redemptions.$inferSelect;
export type InsertRedemption = z.infer<typeof insertRedemptionSchema>;
export type BusinessApplication = typeof businessApplications.$inferSelect;
export type InsertBusinessApplication = z.infer<typeof insertBusinessApplicationSchema>;
export type VenueSnapshot = typeof venueSnapshots.$inferSelect;
export type InsertVenueSnapshot = z.infer<typeof insertVenueSnapshotSchema>;
export const insertVenueSnapshotHistorySchema = createInsertSchema(venueSnapshotHistory).omit({ id: true, snapshotAt: true });
export type VenueSnapshotHistory = typeof venueSnapshotHistory.$inferSelect;
export type InsertVenueSnapshotHistory = z.infer<typeof insertVenueSnapshotHistorySchema>;

export type ClaudeEstimateCache = typeof claudeEstimateCache.$inferSelect;
export const insertClaudeEstimateCacheSchema = createInsertSchema(claudeEstimateCache).omit({ cachedAt: true });
export type InsertClaudeEstimateCache = z.infer<typeof insertClaudeEstimateCacheSchema>;

export type GoogleHistogramHistory = typeof googleHistogramHistory.$inferSelect;
export const insertGoogleHistogramHistorySchema = createInsertSchema(googleHistogramHistory).omit({ id: true, fetchedAt: true });
export type InsertGoogleHistogramHistory = z.infer<typeof insertGoogleHistogramHistorySchema>;

export type ClaudeEstimateHistory = typeof claudeEstimateHistory.$inferSelect;
export const insertClaudeEstimateHistorySchema = createInsertSchema(claudeEstimateHistory).omit({ id: true, recordedAt: true });
export type InsertClaudeEstimateHistory = z.infer<typeof insertClaudeEstimateHistorySchema>;

export type SchedulerState = typeof schedulerState.$inferSelect;
export const insertSchedulerStateSchema = createInsertSchema(schedulerState).omit({ updatedAt: true });
export type InsertSchedulerState = z.infer<typeof insertSchedulerStateSchema>;

export type Friendship = typeof friendships.$inferSelect;
export type InsertFriendship = z.infer<typeof insertFriendshipSchema>;
export type FriendLocationSharing = typeof friendLocationSharing.$inferSelect;
export type InsertFriendLocationSharing = z.infer<typeof insertFriendLocationSharingSchema>;
export type PushSubscription = typeof pushSubscriptions.$inferSelect;
export type InsertPushSubscription = z.infer<typeof insertPushSubscriptionSchema>;

export type CreateVenueRequest = InsertVenue;
export type UpdateVenueRequest = Partial<InsertVenue>;
export type VenueResponse = Venue;

export interface VenuesQueryParams {
  lat?: number;
  lng?: number;
  radius?: number;
}

export const userBadges = pgTable("user_badges", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => users.id).unique(),
  friendsBadge: integer("friends_badge").notNull().default(0),
  profileBadge: integer("profile_badge").notNull().default(0),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertUserBadgeSchema = createInsertSchema(userBadges).omit({
  id: true,
  updatedAt: true,
});

export type InsertUserBadge = z.infer<typeof insertUserBadgeSchema>;
export type SelectUserBadge = typeof userBadges.$inferSelect;

export const favouriteNotificationPrefs = pgTable("favourite_notification_prefs", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => users.id),
  venueId: integer("venue_id").notNull().references(() => venues.id),
  eventsEnabled: boolean("events_enabled").notNull().default(true),
  thresholdEnabled: boolean("threshold_enabled").notNull().default(false),
  thresholdType: text("threshold_type").notNull().default("busy"),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => ({
  userVenueUnique: uniqueIndex("favourite_notification_prefs_user_venue_unique").on(table.userId, table.venueId),
}));

export const insertFavouriteNotificationPrefSchema = createInsertSchema(favouriteNotificationPrefs).omit({
  id: true,
  createdAt: true,
}).extend({
  thresholdType: z.enum(["quiet", "busy", "both"]).default("busy"),
});

export type InsertFavouriteNotificationPref = z.infer<typeof insertFavouriteNotificationPrefSchema>;
export type SelectFavouriteNotificationPref = typeof favouriteNotificationPrefs.$inferSelect;

export const discoveryJobStatus = pgTable("discovery_job_status", {
  id: integer("id").primaryKey().default(1),
  running: boolean("running").notNull().default(false),
  interrupted: boolean("interrupted").notNull().default(false),
  total: integer("total").notNull().default(0),
  searched: integer("searched").notNull().default(0),
  discovered: integer("discovered").notNull().default(0),
  duplicates: integer("duplicates").notNull().default(0),
  errors: integer("errors").notNull().default(0),
  lastRun: timestamp("last_run"),
  currentArea: text("current_area").notNull().default(""),
  recentlyAdded: text("recently_added").array().notNull().default(sql`ARRAY[]::text[]`),
  completedSearches: jsonb("completed_searches").notNull().default(sql`'[]'::jsonb`),
  jobMode: text("job_mode").notNull().default("full"),
  targetAreas: text("target_areas").array().notNull().default(sql`ARRAY[]::text[]`),
});

export type SelectDiscoveryJobStatus = typeof discoveryJobStatus.$inferSelect;
export type InsertDiscoveryJobStatus = typeof discoveryJobStatus.$inferInsert;

export const notifications = pgTable("notifications", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => users.id),
  // friend_request | friend_accept | friend_vibe | venue_busy | venue_quiet | media_reaction | media_removed
  type: text("type").notNull(),
  title: text("title").notNull(),
  body: text("body").notNull(),
  link: text("link"),
  read: boolean("read").notNull().default(false),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => ({
  userCreatedIdx: index("notifications_user_created_idx").on(table.userId, table.createdAt),
}));

export const insertNotificationSchema = createInsertSchema(notifications).omit({
  id: true,
  read: true,
  createdAt: true,
});

export type InsertNotification = z.infer<typeof insertNotificationSchema>;
export type SelectNotification = typeof notifications.$inferSelect;
